import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import { ConfirmProvider } from './components/ConfirmModal';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import Team from './pages/Team';
import TimeTracking from './pages/TimeTracking';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import ClientPortal from './pages/ClientPortal';
import ClientInvoices from './pages/ClientInvoices';
import Attendance from './pages/Attendance';
import Leaves from './pages/Leaves';
import Salary from './pages/Salary';
import HR from './pages/HR';
import Holidays from './pages/Holidays';
import Notifications from './pages/Notifications';
import Workload from './pages/Workload';
import TimeApproval from './pages/TimeApproval';
import ProjectTemplates from './pages/ProjectTemplates';
import ProfitAnalytics from './pages/ProfitAnalytics';
import Invoices from './pages/Invoices';
import PaymentAccounts from './pages/PaymentAccounts';
import Expenses from './pages/Expenses';
import AuditLog from './pages/AuditLog';
import Permissions from './pages/Permissions';
import Clients from './pages/Clients';
import EmployeeAccounts from './pages/EmployeeAccounts';
import Performance from './pages/Performance';
import WFHRequests from './pages/WFHRequests';
import Messages from './pages/Messages';
import Calendar from './pages/Calendar';
import CRM from './pages/CRM';
import Upwork from './pages/Upwork';
import DailyWork from './pages/DailyWork';
import TaskTemplates from './pages/TaskTemplates';
import Onboarding from './pages/Onboarding';
import Training from './pages/Training';
import Shifts from './pages/Shifts';
import RevenueForecast from './pages/RevenueForecast';
import DailyReports from './pages/DailyReports';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
      <ThemeProvider>
      <AuthProvider>
        <SocketProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public */}
                <Route path="/login" element={<Login />} />

                {/* Protected — all roles */}
                <Route path="/"               element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/dashboard"      element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                {/* Client dashboard redirect — /dashboard → /client-portal for clients (handled in Dashboard.js) */}
                <Route path="/projects"       element={<PrivateRoute><Projects /></PrivateRoute>} />
                <Route path="/projects/:id"   element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
                <Route path="/tasks"          element={<PrivateRoute><Tasks /></PrivateRoute>} />
                <Route path="/tasks/:id"      element={<PrivateRoute><TaskDetail /></PrivateRoute>} />
                <Route path="/notifications"  element={<PrivateRoute><Notifications /></PrivateRoute>} />
                <Route path="/settings"       element={<PrivateRoute><Settings /></PrivateRoute>} />

                {/* Client-only */}
                <Route path="/client-portal"  element={<PrivateRoute roles={['client']}><ClientPortal /></PrivateRoute>} />
                <Route path="/my-invoices"    element={<PrivateRoute roles={['client']}><ClientInvoices /></PrivateRoute>} />

                {/* Admin + Team only */}
                <Route path="/team"           element={<PrivateRoute roles={['admin','manager','team']} perm="team"><Team /></PrivateRoute>} />
                <Route path="/time-tracking"  element={<PrivateRoute roles={['admin','manager','team']} perm="time_tracking"><TimeTracking /></PrivateRoute>} />
                <Route path="/attendance"     element={<PrivateRoute roles={['admin','manager','team']} perm="attendance"><Attendance /></PrivateRoute>} />
                <Route path="/leaves"         element={<PrivateRoute roles={['admin','manager','team']} perm="leaves"><Leaves /></PrivateRoute>} />
                <Route path="/salary"         element={<PrivateRoute roles={['admin','manager','team']} perm="salary"><Salary /></PrivateRoute>} />
                <Route path="/hr"             element={<PrivateRoute roles={['admin','manager','team']} perm="team"><HR /></PrivateRoute>} />
                <Route path="/holidays"       element={<PrivateRoute roles={['admin','manager','team']} perm="holidays"><Holidays /></PrivateRoute>} />

                {/* Admin / manager only */}
                <Route path="/analytics"        element={<PrivateRoute roles={['admin','manager']} perm="analytics"><Analytics /></PrivateRoute>} />
                <Route path="/workload"         element={<PrivateRoute roles={['admin','manager']} perm="workload"><Workload /></PrivateRoute>} />
                <Route path="/time-approval"    element={<PrivateRoute roles={['admin','manager']} perm="time_approval"><TimeApproval /></PrivateRoute>} />
                <Route path="/templates"        element={<PrivateRoute roles={['admin','manager']} perm="templates"><ProjectTemplates /></PrivateRoute>} />
                <Route path="/profit-analytics" element={<PrivateRoute roles={['admin','manager']} perm="profit_analytics"><ProfitAnalytics /></PrivateRoute>} />
                <Route path="/invoices"         element={<PrivateRoute roles={['admin','manager']} perm="invoices"><Invoices /></PrivateRoute>} />
                <Route path="/payment-accounts" element={<PrivateRoute roles={['admin','manager']} perm="payment_accounts"><PaymentAccounts /></PrivateRoute>} />
                <Route path="/audit-log"        element={<PrivateRoute roles={['admin','manager']} perm="audit_log"><AuditLog /></PrivateRoute>} />
                <Route path="/permissions"      element={<PrivateRoute roles={['admin','manager']} perm="permissions"><Permissions /></PrivateRoute>} />
                <Route path="/crm"              element={<PrivateRoute roles={['admin','manager']} perm="crm"><CRM /></PrivateRoute>} />
                <Route path="/upwork"           element={<PrivateRoute roles={['admin','manager']} perm="upwork"><Upwork /></PrivateRoute>} />
                <Route path="/daily-work"       element={<PrivateRoute roles={['admin','manager']} perm="daily_work"><DailyWork /></PrivateRoute>} />
                <Route path="/task-templates"   element={<PrivateRoute roles={['admin','manager']} perm="task_templates"><TaskTemplates /></PrivateRoute>} />
                <Route path="/revenue-forecast" element={<PrivateRoute roles={['admin','manager']} perm="revenue_forecast"><RevenueForecast /></PrivateRoute>} />

                {/* Admin + Team (with perm check) */}
                <Route path="/expenses"          element={<PrivateRoute roles={['admin','manager','team']} perm="expenses"><Expenses /></PrivateRoute>} />
                <Route path="/employee-accounts" element={<PrivateRoute roles={['admin','manager','team']} perm="employee_accounts"><EmployeeAccounts /></PrivateRoute>} />
                <Route path="/clients"           element={<PrivateRoute roles={['admin','manager','team']} perm="clients"><Clients /></PrivateRoute>} />
                <Route path="/performance"       element={<PrivateRoute roles={['admin','manager','team']} perm="performance"><Performance /></PrivateRoute>} />
                <Route path="/wfh"               element={<PrivateRoute roles={['admin','manager','team']} perm="wfh"><WFHRequests /></PrivateRoute>} />
                <Route path="/messages"          element={<PrivateRoute roles={['admin','manager','team','client']}><Messages /></PrivateRoute>} />
                <Route path="/calendar"          element={<PrivateRoute roles={['admin','manager','team']} perm="calendar"><Calendar /></PrivateRoute>} />
                <Route path="/onboarding"        element={<PrivateRoute roles={['admin','manager','team']} perm="onboarding"><Onboarding /></PrivateRoute>} />
                <Route path="/training"          element={<PrivateRoute roles={['admin','manager','team']} perm="training"><Training /></PrivateRoute>} />
                <Route path="/shifts"            element={<PrivateRoute roles={['admin','manager','team']} perm="shifts"><Shifts /></PrivateRoute>} />
                <Route path="/daily-reports"     element={<PrivateRoute roles={['admin','manager','team']} perm="daily_reports"><DailyReports /></PrivateRoute>} />

                {/* Redirect */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>

              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: { background: '#363636', color: '#fff' },
                  success: { duration: 3000, iconTheme: { primary: '#48bb78', secondary: '#fff' } },
                  error:   { duration: 4000, iconTheme: { primary: '#f56565', secondary: '#fff' } },
                }}
              />
            </div>
          </Router>
        </SocketProvider>
      </AuthProvider>
      </ThemeProvider>
      </ConfirmProvider>
    </QueryClientProvider>
  );
}

export default App;
